<script setup lang="ts">
import {
  computed,
  nextTick,
  ref,
} from 'vue';

import type { IChangeHistoryMessage } from '@modules/change-history/components/VChangeHistory/types';
import type { IDashboardPlayerCheckPayload } from '@modules/dashboard/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { TRANSLATION_GROUP_DASHBOARD_AFFILIATE } from '@modules/dashboard/constants';
import { useDashboardPlayerCheckQuery } from '@modules/dashboard/queries';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

const dialogVisible = ref<boolean>(false);
const resultMsg = ref<IChangeHistoryMessage>();

const { getTranslation } = useI18n();

function displayResult(message: IChangeHistoryMessage): void {
  resultMsg.value = message;
  dialogVisible.value = true;
}

function onHide(): void {
  resultMsg.value = undefined;
  dialogVisible.value = false;
}

const payload = ref<IDashboardPlayerCheckPayload>({
  cid: '',
});

const query = useDashboardPlayerCheckQuery(payload);
const { isFetching: isLoading, waitFetched } = query;

async function onSubmit(values: IDashboardPlayerCheckPayload): Promise<void> {
  payload.value = { ...values };
  await nextTick();
  const data = await waitFetched();
  displayResult(data);
}

const formSchema = computed<FormSchema<IDashboardPlayerCheckPayload>>(() => ({
  cid: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_DASHBOARD_AFFILIATE, 'CHECK_PLAYER_PLAYERS_NAME').value,
    value: payload.value.cid,
    required: true,
  },
}));
</script>

<template>
  <PCard v-data-qa-auto-id="'TheDashboardPlayerCheck'">
    <template #title>
      {{ $getTranslation(TRANSLATION_GROUP_DASHBOARD_AFFILIATE, 'CHECK_PLAYER_HEADER') }}
    </template>
    <template #content>
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_DASHBOARD_AFFILIATE, 'CHECK_PLAYER_BUTTON')"
        :is-loading="isLoading"
        @submit="onSubmit"
      />
      <VDialog
        :visible="dialogVisible"
        :title="$getTranslation(TRANSLATION_GROUP_DASHBOARD_AFFILIATE, 'CHECK_PLAYER_MODAL_TITLE')"
        @hide="onHide"
      >
        <div>{{ resultMsg ? $t(resultMsg.text, resultMsg.data) : '' }}</div>
      </VDialog>
    </template>
  </PCard>
</template>
